import {
  FloridayThemeProvider,
  GlobalStyleProvider,
} from "@floriday/floriday-ui";
import {
  brandPurple,
  highlightOrange,
} from "@floriday/floriday-ui/es/Styles/colors";

import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen.ts";
import { CssBaseline, StyledEngineProvider } from "@mui/material";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security, useOktaAuth } from "@okta/okta-react";
import { initializeOktaAuth } from "./utils/auth.ts";
import { initializeODataApiClient, initializeRegularApiClient } from "./utils/kiota-client.ts";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n/config";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./services/queryClient.ts";
import { useState, useEffect } from "react";
import { useFeatureFlags } from "./hooks/useFeatureFlag.ts";

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    enabledFeatures: undefined!,
    initializing: true,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function RouterWithContext() {
  const auth = useOktaAuth();
  const accessToken = auth.authState?.accessToken
  const { data: enabledFeatures, isLoading } = useFeatureFlags(accessToken)

  return <RouterProvider router={router} context={{ auth, enabledFeatures, initializing: isLoading }} />;
}

function App() {
  const [oktaAuth, setOktaAuth] = useState<OktaAuth | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    try {
      const auth = initializeOktaAuth();
      initializeODataApiClient();
      initializeRegularApiClient();
      setOktaAuth(auth);
    } catch (err) {
      setError(err as Error);
    }
  }, []);

  if (error) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        <h1>Configuration Error</h1>
        <p>{error.message}</p>
        <p>Please contact your system administrator.</p>
      </div>
    );
  }

  if (!oktaAuth) {
    return (
      <div style={{ padding: '20px' }}>
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <FloridayThemeProvider
      primary={{ main: highlightOrange.main }}
      secondary={{ main: brandPurple.main }}
    >
      <I18nextProvider i18n={i18n}>
        <StyledEngineProvider injectFirst>
          <GlobalStyleProvider>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <QueryClientProvider client={queryClient}>
                <Security 
                  oktaAuth={oktaAuth}
                  restoreOriginalUri={() => {
                    // No-op: callback component handles all navigation
                    // This prevents the duplicate restoreOriginalUri warning
                  }}
                >
                  <RouterWithContext />
                </Security>
              </QueryClientProvider>
            </LocalizationProvider>
          </GlobalStyleProvider>
        </StyledEngineProvider>
      </I18nextProvider>
    </FloridayThemeProvider>
  );
}

export default App;
